import { initInvestorStickyNavScroll } from '../investor-zone/investor-sticky-nav.js';

function initInvestorFaqList(list) {
    const items = list.querySelectorAll('.investor-faq__item');

    const closeItem = (item) => {
        const trigger = item.querySelector('.investor-faq__trigger');
        const panel = item.querySelector('.investor-faq__panel');

        item.classList.remove('is-open');
        trigger?.setAttribute('aria-expanded', 'false');

        if (!panel) return;

        if (panel.style.maxHeight === 'none' || panel.style.maxHeight === '') {
            panel.style.maxHeight = `${panel.scrollHeight}px`;
        }

        panel.getBoundingClientRect();
        panel.style.maxHeight = '0';
    };

    const openItem = (item) => {
        const trigger = item.querySelector('.investor-faq__trigger');
        const panel = item.querySelector('.investor-faq__panel');

        item.classList.add('is-open');
        trigger?.setAttribute('aria-expanded', 'true');

        if (panel) {
            panel.style.maxHeight = `${panel.scrollHeight}px`;
        }
    };

    items.forEach((item) => {
        const trigger = item.querySelector('.investor-faq__trigger');
        const panel = item.querySelector('.investor-faq__panel');

        if (!trigger || !panel) return;

        panel.addEventListener('transitionend', (event) => {
            if (event.propertyName !== 'max-height' || !item.classList.contains('is-open')) return;
            panel.style.maxHeight = 'none';
        });

        trigger.addEventListener('click', () => {
            const isOpen = item.classList.contains('is-open');

            items.forEach((other) => {
                if (other !== item) closeItem(other);
            });

            if (isOpen) {
                closeItem(item);
            } else {
                openItem(item);
            }
        });
    });
}

function normalizePhone(phone) {
    return String(phone || '').replace(/\s+/g, '');
}

function buildDirectionsUrl(address) {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
}

function formatCompanyAddress(company) {
    const street = [company.street, company.house].filter(Boolean).join(' ').trim();
    const cityLine = [company.post_code, company.city].filter(Boolean).join(' ').trim();

    return [street, cityLine].filter(Boolean).join(', ');
}

function mapCompanyToCard(company) {
    return {
        id: company.identifier,
        name: company.company || '',
        address: formatCompanyAddress(company),
        phone: company.phone || '',
        lat: company.lat ? Number(company.lat) : null,
        lng: company.lng ? Number(company.lng) : null,
        distance: company.distance != null ? Number(company.distance) : null,
    };
}

function sortByDistance(items) {
    return [...items].sort((a, b) => {
        if (a.distance != null && b.distance != null) {
            return a.distance - b.distance;
        }

        if (a.distance != null) return -1;
        if (b.distance != null) return 1;

        return 0;
    });
}

let googleMapsLoader = null;

function createMapMarkerIcon(maps, url, size = 32) {
    return {
        url,
        scaledSize: new maps.Size(size, size),
        anchor: new maps.Point(size / 2, size),
    };
}

function loadGoogleMaps(apiKey) {
    if (window.google?.maps) {
        return Promise.resolve(window.google.maps);
    }

    if (!apiKey) {
        return Promise.reject(new Error('Missing Google Maps API key'));
    }

    if (googleMapsLoader) {
        return googleMapsLoader;
    }

    googleMapsLoader = new Promise((resolve, reject) => {
        const callbackName = 'investorWhereBuyMapsInit';
        window[callbackName] = () => resolve(window.google.maps);
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${callbackName}`;
        script.async = true;
        script.onerror = () => reject(new Error('Google Maps failed to load'));
        document.head.appendChild(script);
    });

    return googleMapsLoader;
}

function initInvestorWhereBuyFinder(section) {
    const form = section.querySelector('[data-finder-form]');
    const input = section.querySelector('[data-finder-input]');
    const submitButton = section.querySelector('[data-finder-submit]');
    const resultsWrap = section.querySelector('[data-finder-results]');
    const emptyWrap = section.querySelector('[data-finder-empty]');
    const emptyCount = section.querySelector('[data-finder-empty-count]');
    const radiusWrap = section.querySelector('[data-finder-radius]');
    const list = section.querySelector('[data-finder-list]');
    const autocomplete = section.querySelector('[data-finder-autocomplete]');
    const template = document.getElementById('investorWhereBuyFinderCardTemplate');
    const mapEl = section.querySelector('[data-finder-map]');
    const showAllBtn = section.querySelector('[data-finder-show-all]');

    if (!form || !input || !resultsWrap || !emptyWrap || !list || !template) return;

    const searchUrl = section.dataset.searchUrl || '';
    const autocompleteUrl = section.dataset.autocompleteUrl || '';
    const mapsApiKey = section.dataset.mapsApiKey || '';
    const searchRadiusKm = section.dataset.searchRadiusKm || '30';
    const maxResults = Number.parseInt(section.dataset.maxResults || '6', 10) || 6;
    const showAllLabel = section.dataset.showAll || 'Pokaż więcej';
    const mapMarkerUrl = section.dataset.mapMarkerUrl || '';
    const mapMarkerActiveUrl = section.dataset.mapMarkerActiveUrl || '';
    const msgError = section.dataset.msgError || 'Wystąpił błąd. Spróbuj ponownie później.';
    const msgAutocompleteEmpty = section.dataset.msgAutocompleteEmpty || 'Brak wyników';
    const msgAutocompleteError = section.dataset.msgAutocompleteError || 'Błąd pobierania danych';
    const radiusTitle = section.dataset.radiusTitle || '';
    const radiusTextTemplate = section.dataset.radiusText || '';
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';

    let autocompleteTimeout = null;
    let mapInstance = null;
    let mapMarkers = new Map();
    let activeMarker = null;
    let submitLabel = submitButton?.textContent || '';
    let displayedItems = [];
    let visibleCount = maxResults;

    const setPanelState = (state) => {
        resultsWrap.hidden = state !== 'results';
        emptyWrap.hidden = state !== 'empty';
    };

    const setLoading = (isLoading) => {
        if (!submitButton) return;
        submitButton.disabled = isLoading;
        submitButton.textContent = isLoading ? 'Szukam…' : submitLabel;
    };

    const hideAutocomplete = () => {
        if (!autocomplete) return;
        autocomplete.hidden = true;
        autocomplete.innerHTML = '';
    };

    const showAutocompleteMessage = (text, disabled = true) => {
        if (!autocomplete) return;
        autocomplete.innerHTML = '';
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'investor-where-buy__finder-autocomplete-item';
        if (disabled) item.classList.add('is-disabled');
        item.textContent = text;
        item.disabled = disabled;
        autocomplete.appendChild(item);
        autocomplete.hidden = false;
    };

    const formatAutocompleteLabel = (item) => {
        if (item.rmName === 'post_code') {
            return `${item.name} (${item.api_voivodeship_name})`;
        }

        if (item.post_code) {
            return `${item.name} (${item.post_code}, ${item.api_voivodeship_name})`;
        }

        return `${item.name} (${item.api_voivodeship_name})`;
    };

    const getAutocompleteSearchValue = (item) => {
        if (item.rmName === 'post_code') {
            return item.name;
        }

        return item.name;
    };

    const setMarkerIcon = (marker, active = false) => {
        if (!marker || !window.google?.maps) return;

        const url = active ? (mapMarkerActiveUrl || mapMarkerUrl) : mapMarkerUrl;
        if (!url) return;

        const size = active ? 40 : 32;
        marker.setIcon(createMapMarkerIcon(window.google.maps, url, size));
    };

    const setActiveCard = (card) => {
        list.querySelectorAll('[data-finder-card]').forEach((item) => {
            item.classList.toggle('is-active', item === card);
        });

        if (!card || !mapEl) return;

        const companyId = card.dataset.companyId || '';
        mapEl.dataset.activeId = companyId;

        if (activeMarker) {
            setMarkerIcon(activeMarker, false);
            activeMarker = null;
        }

        const marker = mapMarkers.get(companyId);
        if (marker && mapInstance) {
            activeMarker = marker;
            setMarkerIcon(marker, true);
            mapInstance.panTo(marker.getPosition());
        }
    };

    const getVisibleListItems = (items) => {
        if (!maxResults) {
            return items;
        }

        return items.slice(0, visibleCount);
    };

    const updateShowAllButton = (totalCount) => {
        if (!showAllBtn) return;

        showAllBtn.hidden = totalCount <= visibleCount;
        showAllBtn.textContent = showAllLabel;
    };

    const focusItemOnMap = (item) => {
        let card = list.querySelector(`[data-company-id="${CSS.escape(String(item.id))}"]`);

        if (!card && visibleCount < displayedItems.length) {
            visibleCount = displayedItems.length;
            renderListCards(displayedItems);
            card = list.querySelector(`[data-company-id="${CSS.escape(String(item.id))}"]`);
        }

        if (card) setActiveCard(card);
    };

    const renderListCards = (items) => {
        const visibleItems = getVisibleListItems(items);

        list.innerHTML = '';

        visibleItems.forEach((item, index) => {
            const node = template.content.firstElementChild.cloneNode(true);

            node.dataset.companyId = item.id || `company-${index}`;
            node.querySelector('[data-finder-card-name]').textContent = item.name || '';
            node.querySelector('[data-finder-card-address]').textContent = item.address || '';
            node.querySelector('[data-finder-card-phone]').textContent = item.phone || '';

            const phone = normalizePhone(item.phone);
            const callLink = node.querySelector('[data-finder-card-call]');
            const directionsLink = node.querySelector('[data-finder-card-directions]');
            const focusButton = node.querySelector('[data-finder-card-focus]');
            const phoneRow = node.querySelector('[data-finder-card-phone]')?.closest('.investor-where-buy__finder-card-row');

            if (callLink) {
                if (phone) {
                    callLink.href = `tel:${phone}`;
                } else {
                    callLink.hidden = true;
                }
            }

            if (!item.phone && phoneRow) {
                phoneRow.hidden = true;
            }

            if (directionsLink && item.address) {
                directionsLink.href = buildDirectionsUrl(item.address);
            }

            node.addEventListener('click', (event) => {
                if (event.target.closest('a, button')) return;
                setActiveCard(node);
            });

            focusButton?.addEventListener('click', () => {
                setActiveCard(node);
                mapEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            });

            list.appendChild(node);

            if (index === 0) {
                setActiveCard(node);
            }
        });

        updateShowAllButton(items.length);
    };

    const renderResults = (items) => {
        displayedItems = items;
        renderListCards(items);
        updateMap(items);
    };

    const updateRadiusNotice = (nothingFound) => {
        if (!radiusWrap) return;

        if (!nothingFound) {
            radiusWrap.hidden = true;
            return;
        }

        const titleEl = radiusWrap.querySelector('.investor-where-buy__finder-radius-title');
        const textEl = radiusWrap.querySelector('.investor-where-buy__finder-radius-text');

        if (titleEl && radiusTitle) {
            titleEl.textContent = radiusTitle;
        }

        if (textEl && radiusTextTemplate) {
            textEl.textContent = radiusTextTemplate.replace(':radius', searchRadiusKm);
        }

        radiusWrap.hidden = false;
    };

    const updateMap = (items) => {
        if (!mapEl || !mapsApiKey) return;

        const mappable = items.filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng));

        loadGoogleMaps(mapsApiKey)
            .then((maps) => {
                if (!mapInstance) {
                    mapInstance = new maps.Map(mapEl, {
                        center: { lat: 52.069, lng: 19.48 },
                        zoom: 6,
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: false,
                    });
                    mapEl.classList.add('is-ready');
                }

                mapMarkers.forEach((marker) => marker.setMap(null));
                mapMarkers.clear();
                activeMarker = null;

                if (!mappable.length) {
                    mapInstance.setCenter({ lat: 52.069, lng: 19.48 });
                    mapInstance.setZoom(6);
                    return;
                }

                const bounds = new maps.LatLngBounds();

                mappable.forEach((item) => {
                    const position = { lat: item.lat, lng: item.lng };
                    const marker = new maps.Marker({
                        map: mapInstance,
                        position,
                        title: item.name,
                        ...(mapMarkerUrl && {
                            icon: createMapMarkerIcon(maps, mapMarkerUrl, 32),
                        }),
                    });

                    marker.addListener('click', () => {
                        focusItemOnMap(item);
                    });

                    mapMarkers.set(String(item.id), marker);
                    bounds.extend(position);
                });

                if (mappable.length === 1) {
                    mapInstance.setCenter(bounds.getCenter());
                    mapInstance.setZoom(13);
                } else {
                    mapInstance.fitBounds(bounds, 48);
                }

                const firstCard = list.querySelector('[data-finder-card]');
                if (firstCard) setActiveCard(firstCard);
            })
            .catch((error) => {
                console.error('Mapa Google:', error);
            });
    };

    const initMap = () => {
        updateMap([]);
    };

    const runSearch = (localizationId = '', localizationName = input.value.trim()) => {
        hideAutocomplete();

        if (!localizationName) {
            if (emptyCount) emptyCount.textContent = '0';
            displayedItems = [];
            visibleCount = maxResults;
            updateShowAllButton(0);
            setPanelState('empty');
            updateRadiusNotice(false);
            return;
        }

        if (!searchUrl) return;

        visibleCount = maxResults;
        setLoading(true);

        fetch(searchUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                'X-Requested-With': 'XMLHttpRequest',
                ...(csrfToken && { 'X-CSRF-Token': csrfToken }),
            },
            body: new URLSearchParams({
                localizationName,
                localizationId: localizationId || '',
            }),
        })
            .then((response) => response.json())
            .then((data) => {
                if (data.code !== 200) {
                    throw new Error(data.message || msgError);
                }

                const companies = Array.isArray(data.companiesForMap)
                    ? data.companiesForMap.filter(Boolean)
                    : [];
                const items = sortByDistance(companies.map(mapCompanyToCard));

                updateRadiusNotice(Boolean(data.nothingFound));

                if (!items.length) {
                    if (emptyCount) emptyCount.textContent = '0';
                    displayedItems = [];
                    updateShowAllButton(0);
                    setPanelState('empty');
                    updateMap([]);
                    return;
                }

                if (data.localizationName) {
                    input.value = data.localizationName;
                }

                setPanelState('results');
                renderResults(items);
                updateMap(items);
            })
            .catch((error) => {
                console.error('Finder:', error);
                alert(msgError);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    showAllBtn?.addEventListener('click', () => {
        visibleCount += maxResults;
        renderListCards(displayedItems);
    });

    form.addEventListener('submit', (event) => {
        event.preventDefault();
        runSearch();
    });

    input.addEventListener('input', () => {
        const query = input.value.trim();
        clearTimeout(autocompleteTimeout);

        if (!autocomplete || !autocompleteUrl || query.length < 2) {
            hideAutocomplete();
            return;
        }

        autocompleteTimeout = setTimeout(() => {
            fetch(`${autocompleteUrl}?query=${encodeURIComponent(query)}`)
                .then((response) => response.json())
                .then((data) => {
                    const items = Array.isArray(data) ? data : Object.values(data || {});

                    if (!items.length) {
                        showAutocompleteMessage(msgAutocompleteEmpty);
                        return;
                    }

                    autocomplete.innerHTML = '';

                    items.forEach((item) => {
                        const button = document.createElement('button');
                        button.type = 'button';
                        button.className = 'investor-where-buy__finder-autocomplete-item';
                        button.textContent = formatAutocompleteLabel(item);
                        button.dataset.localizationId = item.id || '';
                        button.dataset.localizationName = getAutocompleteSearchValue(item);
                        autocomplete.appendChild(button);
                    });

                    autocomplete.hidden = false;
                })
                .catch(() => {
                    showAutocompleteMessage(msgAutocompleteError);
                });
        }, 200);
    });

    autocomplete?.addEventListener('click', (event) => {
        const item = event.target.closest('.investor-where-buy__finder-autocomplete-item');
        if (!item || item.classList.contains('is-disabled')) return;

        input.value = item.dataset.localizationName || item.textContent;
        hideAutocomplete();
        runSearch(item.dataset.localizationId || '', item.dataset.localizationName || input.value.trim());
    });

    document.addEventListener('click', (event) => {
        if (event.target.closest('[data-finder-input], [data-finder-autocomplete]')) return;
        hideAutocomplete();
    });

    setPanelState('idle');
    initMap();
}

document.addEventListener('DOMContentLoaded', () => {
    initInvestorStickyNavScroll(
        '.investor-where-buy__sticky-nav',
        'a[href="#investorWhereBuyFinder"]',
    );

    const list = document.querySelector('#investorWhereBuyFaq');
    if (list) initInvestorFaqList(list);

    const finder = document.querySelector('[data-finder]');
    if (finder) initInvestorWhereBuyFinder(finder);
});
